/**
 * Pricing a composition that belongs to no portfolio yet — one being created, or a draft. The pricing
 * endpoint answers only for a portfolio, so each identifier is sized on Cold War for one share instead
 * (`../position-sizing`) and only the price and the status of that answer are kept: Cold War prices
 * the same way for both, and the share count asked for is no position anyone holds.
 */
import type { InstrumentPricing } from "@eu.mdotm.api/sphere";
import { parallelizeWithAllSettled } from "$root/utils/promise";
import { parseSizingAnswer, type SizingAnswer } from "../position-sizing/answer";
import { positionSizingRepository } from "../position-sizing/repository";

/** How many identifiers are sized at once. */
const SIZING_CONCURRENCY = 3;

/**
 * What a sizing answer says about the identifier's price, in the pricing endpoint's terms: `OK` with a
 * price is held in shares at it, `NOT_SHAREABLE` is held by value, and anything else — no close, not in
 * Cold War's market data, an `OK` without a price — is unpriced. A status the editor does not know fails
 * the sizing, which leaves the identifier out.
 */
function pricingFromSizing(identifier: string, answer: SizingAnswer): InstrumentPricing {
	if (answer.status === "OK" && answer.price != null) {
		return { identifier, holdingMode: "SHARES", price: answer.price, priceDate: answer.priceDate };
	}
	if (answer.status === "NOT_SHAREABLE") {
		return { identifier, holdingMode: "WEIGHT" };
	}
	if (answer.status === "NO_PRICE" || answer.status === "NOT_IN_MARKET_DATA") {
		return { identifier, holdingMode: "UNPRICED", unpricedReason: answer.status };
	}
	return { identifier, holdingMode: "UNPRICED" };
}

/**
 * Prices `identifiers` at `referenceDate` in `currency`, for a portfolio of `aum`. An identifier whose
 * sizing failed is left out of the answer, which the caller reads as unpriced — one failure does not
 * strike out the others.
 */
export async function priceWithoutPortfolio(params: {
	identifiers: string[];
	referenceDate: string;
	currency: string;
	aum: number;
}): Promise<InstrumentPricing[]> {
	const { identifiers, referenceDate, currency, aum } = params;
	const answers = await parallelizeWithAllSettled(
		identifiers.map(
			(ticker) => async () =>
				parseSizingAnswer(
					await positionSizingRepository.deriveInstrumentValue(undefined, {
						ticker,
						currency,
						amount: aum,
						referenceDate,
						shares: 1,
					}),
				),
		),
		{ concurrency: SIZING_CONCURRENCY },
	);
	return answers.flatMap((answer, i) =>
		answer.status === "fulfilled" ? [pricingFromSizing(identifiers[i], answer.value)] : [],
	);
}
